import axios, { AxiosAdapter, AxiosError, AxiosInstance } from 'axios';

import { config } from '@/config/environment';
import { REQUEST_TIMEOUT_MS } from '@/services/http';
import { ProjectTemplate, Team, TeamMember } from '@/types';
import { CONFIRM_ADDRESS_PATH } from '@/utils/confirmationToken';

class ApiClient {
  private readonly instance: AxiosInstance;

  constructor() {
    this.instance = axios.create({
      baseURL: config.backofficeApiUrl,
      withCredentials: true,
      timeout: REQUEST_TIMEOUT_MS,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  /**
   * Demo mode only (src/demo): swap the transport for the canned-data
   * adapter. Every method, transform and interceptor keeps running — only
   * the network goes away.
   */
  installDemoAdapter(adapter: AxiosAdapter): void {
    this.instance.defaults.adapter = adapter;
  }

  private setupInterceptors() {
    // Response interceptor to handle errors
    this.instance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError<{ error?: string }>) => {
        // Handle 401 unauthorized
        if (error.response?.status === 401) {
          // Don't redirect if we're already on auth-related pages
          const currentPath = window.location.pathname;
          const authPaths = ['/login', '/auth/callback', '/bounce', CONFIRM_ADDRESS_PATH];

          if (!authPaths.includes(currentPath)) {
            // Clear any stored tokens
            localStorage.removeItem('authToken');
            sessionStorage.removeItem('authToken');
            localStorage.removeItem('tokenExpiry');
            sessionStorage.removeItem('tokenExpiry');

            // Only redirect if we're not already heading to login
            if (currentPath !== '/login') {
              window.location.href = '/login';
            }
          }
          return Promise.reject(new Error('Unauthorized'));
        }

        // Handle 400+ errors with error message
        if (error.response && error.response.status >= 400) {
          const errorMessage = error.response.data?.error ?? `Error ${error.response.status}`;

          // For 500 errors, only show details in development
          if (error.response.status >= 500 && !config.isDevelopment) {
            return Promise.reject(new Error('Server error'));
          }

          return Promise.reject(new Error(errorMessage));
        }

        // Network or other errors
        const rejectionError = error instanceof Error ? error : new Error(String(error));
        return Promise.reject(rejectionError);
      }
    );
  }

  // Auth endpoints
  async login(email: string, password: string) {
    const response = await this.instance.post('/nexus/login/', {
      email,
      password,
    });
    return response.data;
  }

  async logout() {
    try {
      await this.instance.post('/nexus/logout/');
    } catch (error) {
      // Continue with logout even if API call fails
      // Error is intentionally swallowed here
    }
  }

  async getCurrentUser() {
    const response = await this.instance.get('/nexus/me/');
    return response.data;
  }

  // Team endpoints
  async getTeams(): Promise<Team[]> {
    const response = await this.instance.get('/api/teams/knox/');
    return response.data;
  }

  // Every assignable administrator, regardless of team (admin-only on the
  // backoffice). Authenticated by the user's Knox session cookie via
  // `withCredentials`. Used for the inbox "Assign to…" picker.
  async getAdministrators(): Promise<TeamMember[]> {
    const response = await this.instance.get('/api/teams/knox/administrators/');
    return response.data;
  }

  // Project endpoints
  async getMyProjectTemplates(): Promise<ProjectTemplate[]> {
    const response = await this.instance.get('/api/projects/my_templates');
    return response.data;
  }

  // Generic request methods
  get(url: string, config?: any) {
    return this.instance.get(url, config);
  }

  post(url: string, data?: any, config?: any) {
    return this.instance.post(url, data, config);
  }

  put(url: string, data?: any, config?: any) {
    return this.instance.put(url, data, config);
  }

  delete(url: string, config?: any) {
    return this.instance.delete(url, config);
  }
}

export const apiClient = new ApiClient();
